import * as pdfjsLib from 'pdfjs-dist'
import type { PDFPageProxy } from 'pdfjs-dist/types/src/display/api'

interface Params {
  url: string
  width: Ref<number>
  height: Ref<number>
  currentPdfPage: Ref<number>
  onRender?(dimensions: Size): void
}

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

export function usePdfHotspot({ url, width, height, currentPdfPage, onRender = () => {} }: Params) {
  const pdfCanvas = ref<HTMLCanvasElement | null>(null)
  const hotspotDimensions = ref<{ width: number; height: number }>({ width: width.value, height: height.value })

  watch([width, height], ([_width, height]: [number, number]) => {
    if (!pdfCanvas.value) return
    hotspotDimensions.value = _getDimensions({ height, pdfWidth: pdfCanvas.value.width, pdfHeight: pdfCanvas.value.height })
  })

  watch([pdfCanvas, currentPdfPage], async ([pdfCanvas, currentPdfPage]: [HTMLCanvasElement | null, number]) => {
    if (!pdfCanvas) return

    const pdfDoc = await pdfjsLib.getDocument(url).promise
    const page = await pdfDoc.getPage(currentPdfPage ? currentPdfPage + 1 : 1)
    const pageDimensions = _getPageDimensions(page)
    const viewport = page.getViewport(pageDimensions)

    pdfCanvas.width = viewport.width
    pdfCanvas.height = viewport.height
    hotspotDimensions.value = _getDimensions({ height: height.value, pdfWidth: viewport.width, pdfHeight: viewport.height })

    page.render({
      viewport,
      canvasContext: pdfCanvas.getContext('2d') as CanvasRenderingContext2D,
    })

    if (pageDimensions.width && pageDimensions.height) {
      onRender({ width: pageDimensions.width, height: pageDimensions.height })
    }
  })

  function _getDimensions({ height, pdfWidth, pdfHeight }: { height: number; pdfWidth: number; pdfHeight: number }) {
    const aspectRatio = pdfWidth / pdfHeight
    const w = height * aspectRatio
    const h = w / aspectRatio

    return { width: w, height: h }
  }

  function _getPageDimensions(page: PDFPageProxy) {
    if (page._pageInfo.rotate !== 0) {
      return { scale: 0.5 }
    }

    const pdfSize = page.getViewport({ scale: 1 })
    return {
      scale: Math.min(window.innerWidth / pdfSize.width, window.innerHeight / pdfSize.height),
      width: pdfSize.width,
      height: pdfSize.height,
    }
  }

  return {
    pdfCanvas,
    hotspotDimensions: readonly(hotspotDimensions),
  }
}
